'use client';

import { useCountryContext } from '@/contexts/CountryContext';
import useIsMobile from '@/hooks/useIsMobile';
import { eventTracker } from '@/lib/events/cjrEventTracker';
import { eventTracker as CJRCoursePageEvent } from '@/lib/events/events';
import { Country } from '@/types/types';
import { APP_METHODS, CameUsing, HeaderLinkTitle } from '@/utils/enums';
import { invokeAppMethod, isFromApp } from '@/utils/utils';
import { Typography } from '@pw-tech/omni-ui';
import clsx from 'clsx';
import Image from 'next/image';
import { usePathname, useRouter } from 'next/navigation';
import React, { useMemo } from 'react';
import { HeadersProps } from '../schema';
import MenuDrawer from './MenuDawer';

const CjrHeader = ({
  webLogo = '',
  mWebLogo = '',
  header = {},
}: {
  header: HeadersProps;
  webLogo: string;
  mWebLogo: string;
}) => {
  const router = useRouter();
  const pathname = usePathname() || '';
  const initialPathname = pathname?.split('/')[1];
  const { countryList } = useCountryContext();

  const fullPath = typeof window !== 'undefined' ? window.location.origin + pathname : null;

  const isPageGlobal = useMemo(() => {
    return !countryList?.some(
      (country: Country) => country?.countryGroup?.toLowerCase() === initialPathname
    );
  }, [initialPathname, countryList]);

  const defautCountry = useMemo(() => {
    return countryList?.find(
      (country: Country) => country?.countryGroup?.toLowerCase() === initialPathname
    );
  }, [countryList, initialPathname]);

  const { isMobile } = useIsMobile();

  const handleLogoClick = () => {
    if (pathname === '/') {
      return;
    }
    router.push('/');
    eventTracker.homePageVisit({
      came_from: 'logo_click',
    });
  };

  const handleLoginBtnClick = (e: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>) => {
    eventTracker.loginClick({
      from: 'login_cta',
    });

    if (isFromApp()) {
      e?.preventDefault?.();
      invokeAppMethod(APP_METHODS.OPEN_AUTH_SCREEN);
    }
  };

  const isMarketingPath = pathname.includes('/marketing') || pathname.includes('/after-payment');

  const isMobileMarketingPath = pathname.includes('/marketing');

  const handleLinkClick = (url: string, title: string) => {
    // Track Activity Kits click with Moe & GA
    if (title === HeaderLinkTitle.ACTIVITY_KITS) {
      const platform = isMobile ? 'mweb' : 'web';
      eventTracker.storeClick({
        journey: 'prelogin',
        platform,
        screen: 'home_header',
      });
    }

    if (title.toLowerCase() !== 'home') {
      CJRCoursePageEvent.headerItemClick({
        item_name: title,
        page_link: fullPath as string,
      });
    }
    if (url) {
      if (typeof window !== 'undefined') {
        const newUrl = new URL(url);
        newUrl.searchParams.set('cameUsing', CameUsing.HEADER);
        window.location.href = newUrl.toString();
      }
    }
    // by default it will redirect to /in/school-curriculum or /kw/mental-maths /mental-maths as per the title
    else {
      if (isPageGlobal) {
        if (typeof window !== 'undefined') {
          const urlNew = new URL(window.location.href);
          urlNew.searchParams.set('cameUsing', CameUsing.HEADER);

          const path = `/${title.split(' ').join('-').toLowerCase()}`;
          window.location.href = `${path}?${urlNew.searchParams.toString()}`;

          return;
        }
      }
      if (typeof window !== 'undefined') {
        const urlNew = new URL(window.location.href);
        urlNew.searchParams.set('cameUsing', CameUsing.HEADER);

        const path = `${initialPathname ? `/${initialPathname}` : ''}/${title.split(' ').join('-').toLowerCase()}`;
        window.location.href = `${path}?${urlNew.searchParams.toString()}`;
      }
    }
  };

  return (
    <div className="mx-auto max-w-6xl px-3 w-full md:px-6 flex justify-between items-center z-[51]">
      <div className="flex flex-row gap-4 items-center">
        {isMobile && (header?.links || []).length > 0 && !isFromApp() ? (
          <MenuDrawer
            links={header?.links || []}
            handleLinkClick={handleLinkClick}
            logo={mWebLogo}
            handleLogoClick={handleLogoClick}
            handleLoginBtnClick={handleLoginBtnClick}
            loginButton={header?.loginButton}
          />
        ) : null}

        <div className="flex gap-3 items-center md:gap-6 py-4">
          <div
            className={`relative hidden md:block w-[177px] h-[48px]${!isMarketingPath ? ' cursor-pointer' : ''}`}
            onClick={!isMarketingPath ? handleLogoClick : undefined}
          >
            <Image
              src={webLogo}
              alt="Logo"
              width={354}
              height={96}
              priority
              sizes="(max-width: 768px) 139px, 354px"
            />
          </div>
          <div
            className={clsx(
              'relative md:hidden w-[139px] h-[36px] md:w-[177px] md:h-[48px]',
              !isMobileMarketingPath && 'cursor-pointer'
            )}
            onClick={!isMobileMarketingPath ? handleLogoClick : undefined}
          >
            <Image
              src={mWebLogo}
              alt="Logo"
              width={139}
              height={36}
              priority
              sizes="(max-width: 768px) 139px, 354px"
            />
          </div>
        </div>
        {(header?.links || []).length > 0 && !isMobile ? (
          <div className="flex items-center h-full">
            {header?.links?.map(link => (
              <div
                key={link.title}
                className="h-full py-7 px-6 border-b-2 border-transparent cursor-pointer"
                tabIndex={0}
                role="button"
                onClick={() => handleLinkClick(link.url, link.title)}
              >
                <div className="flex items-center gap-2">
                  {link.title === HeaderLinkTitle.ACTIVITY_KITS && (
                    <Image
                      src="/v2/pendulum.svg"
                      alt="Activity Kits Icon"
                      width={20}
                      height={20}
                      className="object-contain"
                      unoptimized
                    />
                  )}
                  <Typography variant="regular" color="#1b2124" weight="semi-bold">
                    {link.title}
                  </Typography>
                </div>
              </div>
            ))}
          </div>
        ) : null}
      </div>
      {header?.loginButton && (
        <a
          href={initialPathname ? `/${initialPathname}/login` : '/login'}
          id="login-register-btn-header-cjr"
          onClick={handleLoginBtnClick}
          className="md:py-3 py-[10px] no-underline text-[#ff6d0a] border border-[#ff6d0a] rounded-md font-semibold text-sm md:text-base leading-none select-none px-7 md:px-8 min-w-[120px] min-h-[48px] inline-flex items-center justify-center relative"
          aria-label="Login or register"
        >
          <span>Login/Register</span>
        </a>
      )}
    </div>
  );
};

export default CjrHeader;
